import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Paper,
    Chip,
    LinearProgress,
    CircularProgress,
    Alert
} from '@mui/material';
import { CheckCircle, TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/app';
import 'firebase/firestore';

// Same 8 statements as in Onboarding.js
const SELF_ASSESSMENT_STATEMENTS = [
    {
        id: "Q1",
        label:   "Je comprends souvent plus que je n'ose dire.",
        labelEn: "I often understand more than I dare to say.",
        weights: { comprehension: 3, speaking: 1, interaction: 1, strategy: 1, confidence: 0 },
        signal: "prudent"
    },
    {
        id: "Q2",
        label:   "Je cherche mes mots même quand mon idée est claire dans ma tête.",
        labelEn: "I struggle to find my words even when the idea is clear in my head.",
        weights: { comprehension: 2, speaking: 2, interaction: 1, strategy: 1, confidence: 1 },
        signal: "functional"
    },
    {
        id: "Q3",
        label:   "En réunion ou en groupe, je participe peu même quand je suis capable de suivre la conversation.",
        labelEn: "In meetings or groups, I participate little even when I can follow the conversation.",
        weights: { comprehension: 2, speaking: 1, interaction: 0, strategy: 1, confidence: 0 },
        signal: "prudent"
    },
    {
        id: "Q4",
        label:   "J'arrive généralement à faire passer mon message, mais je manque parfois de précision ou d'impact.",
        labelEn: "I generally get my message across, but sometimes lack precision or impact.",
        weights: { comprehension: 2, speaking: 2, interaction: 2, strategy: 2, confidence: 2 },
        signal: "functional"
    },
    {
        id: "Q5",
        label:   "Quand la pression monte, ma fluidité baisse rapidement.",
        labelEn: "When pressure rises, my fluency drops quickly.",
        weights: { comprehension: 1, speaking: 1, interaction: 1, strategy: 1, confidence: 0 },
        signal: "reactive"
    },
    {
        id: "Q6",
        label:   "Je suis plutôt à l'aise dans les situations professionnelles connues ou prévisibles.",
        labelEn: "I'm quite comfortable in familiar or predictable professional situations.",
        weights: { comprehension: 2, speaking: 2, interaction: 2, strategy: 0, confidence: 3 },
        signal: "functional"
    },
    {
        id: "Q7",
        label:   "Je suis capable de m'ajuster à mon interlocuteur, de clarifier ou de reformuler au besoin.",
        labelEn: "I'm able to adapt to my interlocutor, clarify or rephrase when needed.",
        weights: { comprehension: 2, speaking: 2, interaction: 3, strategy: 3, confidence: 2 },
        signal: "influential"
    },
    {
        id: "Q8",
        label:   "Je prends la parole même si ce n'est pas parfait.",
        labelEn: "I speak up even if it's not perfect.",
        weights: { comprehension: 1, speaking: 2, interaction: 3, strategy: 2, confidence: 3 },
        signal: "influential"
    }
];

const AUTO_EVAL_CONFIG = {
    dimensionMax: { comprehension: 7, speaking: 6, interaction: 8, strategy: 7, confidence: 7 },
    globalWeights: { comprehension: 0.20, speaking: 0.25, interaction: 0.20, strategy: 0.20, confidence: 0.15 }
};

const calculateAutoEval = (selectedIds, lang = "fr") => {
    if (selectedIds.length !== 3) return null;
    const selected = SELF_ASSESSMENT_STATEMENTS.filter(s => selectedIds.includes(s.id));

    const rawScores = { comprehension: 0, speaking: 0, interaction: 0, strategy: 0, confidence: 0 };
    const signalCounts = { careful: 0, steady: 0, emerging: 0, confident: 0 };

    selected.forEach(q => {
        Object.keys(rawScores).forEach(dim => rawScores[dim] += q.weights[dim]);
        // Map old signal names to new ones
        const signalMap = { prudent: "careful", functional: "steady", reactive: "emerging", influential: "confident" };
        signalCounts[signalMap[q.signal]] += 1;
    });

    const normalizedScores = {};
    Object.keys(rawScores).forEach(dim => {
        normalizedScores[dim] = Math.round((rawScores[dim] / AUTO_EVAL_CONFIG.dimensionMax[dim]) * 100);
    });

    const globalScore = Math.round(
        normalizedScores.comprehension * AUTO_EVAL_CONFIG.globalWeights.comprehension +
        normalizedScores.speaking * AUTO_EVAL_CONFIG.globalWeights.speaking +
        normalizedScores.interaction * AUTO_EVAL_CONFIG.globalWeights.interaction +
        normalizedScores.strategy * AUTO_EVAL_CONFIG.globalWeights.strategy +
        normalizedScores.confidence * AUTO_EVAL_CONFIG.globalWeights.confidence
    );

    let typeKey = "emerging";
    const entries = Object.entries(signalCounts).sort((a, b) => b[1] - a[1]);
    if (entries[0][1] > entries[1][1]) {
        typeKey = entries[0][0];
    } else {
        if (normalizedScores.confidence >= 70 && normalizedScores.interaction >= 70) typeKey = "confident";
        else if (normalizedScores.speaking >= 60 && normalizedScores.comprehension >= 60) typeKey = "steady";
        else if (normalizedScores.comprehension >= 60 && normalizedScores.confidence < 50) typeKey = "careful";
    }

    return {
        selected_ids: selectedIds,
        raw_scores: rawScores,
        normalized_scores: normalizedScores,
        global_score: globalScore,
        communicator_type: typeKey,
        created_at: new Date()
    };
};

const FinalAutoEvaluationModal = ({ open, profile, auth, onClose, onComplete }) => {
    const { t, i18n } = useTranslation();
    const [selectedIds, setSelectedIds] = useState([]);
    const [testCompleted, setTestCompleted] = useState(false);
    const [autoEvalResult, setAutoEvalResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const autoEvalLang = profile?.learningLanguage === "fr" ? "en" : "fr";
    const ae = autoEvalLang === "en";

    const handleSelectStatement = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(prev => prev.filter(x => x !== id));
        } else if (selectedIds.length < 3) {
            setSelectedIds(prev => [...prev, id]);
        }
    };

    const handleComplete = async () => {
        if (!testCompleted || !autoEvalResult) return;
        setLoading(true);
        try {
            await firebase.firestore().collection('users').doc(auth?.uid).update({
                autoEvaluation_final: autoEvalResult,
                final_eval_reminder_sent: false
            });
            onComplete && onComplete(autoEvalResult);
            onClose();
        } catch (err) {
            console.error("Error saving final auto-eval:", err);
            setError("Failed to save. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
        >
            <DialogTitle sx={{ fontWeight: 800, mb: 2 }}>
                {ae ? "Your Final Self-Assessment" : "Ton auto-évaluation finale"}
            </DialogTitle>

            <DialogContent>
                {!testCompleted ? (
                    <Box>
                        <Typography variant="body2" color="textSecondary" mb={3}>
                            {ae
                                ? "Choose 3 statements that best describe you NOW (compared to the beginning)."
                                : "Choisis les 3 énoncés qui te décrivent LE MIEUX MAINTENANT (par rapport au début)."}
                        </Typography>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="caption" fontWeight={700} color={selectedIds.length === 3 ? "success.main" : "textSecondary"}>
                                {ae ? `${selectedIds.length} / 3 selected` : `${selectedIds.length} / 3 sélectionnés`}
                            </Typography>
                            {selectedIds.length === 3 && (
                                <Chip label={ae ? "Ready" : "Prêt"} size="small" color="success" sx={{ fontWeight: 800 }} />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {SELF_ASSESSMENT_STATEMENTS.map((q) => {
                                const isSelected = selectedIds.includes(q.id);
                                return (
                                    <Paper
                                        key={q.id}
                                        elevation={0}
                                        onClick={() => handleSelectStatement(q.id)}
                                        sx={{
                                            p: 2,
                                            border: '2px solid',
                                            borderColor: isSelected ? '#bfd56c' : '#f1f5f9',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            bgcolor: isSelected ? 'rgba(191, 213, 108, 0.05)' : 'white',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: isSelected ? '#bfd56c' : '#cbd5e1'
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                                            <Box
                                                sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    border: '2px solid',
                                                    borderColor: isSelected ? '#bfd56c' : '#e2e8f0',
                                                    bgcolor: isSelected ? '#bfd56c' : 'transparent',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    mt: 0.3
                                                }}
                                            >
                                                {isSelected && <CheckCircle sx={{ fontSize: 16, color: '#0f172a' }} />}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={isSelected ? 700 : 500}
                                                color={isSelected ? '#0f172a' : '#334155'}
                                            >
                                                {ae ? q.labelEn : q.label}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                ) : (
                    <Box textAlign="center">
                        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(191, 213, 108, 0.1)', border: '2px solid #bfd56c', mb: 3 }}>
                            <Typography variant="overline" color="secondary.main" fontWeight={800} sx={{ letterSpacing: 1.5 }}>
                                {ae ? `Final Score: ${autoEvalResult.global_score}/100` : `Score final : ${autoEvalResult.global_score}/100`}
                            </Typography>
                            <Typography variant="h5" fontWeight={800} mt={1} mb={2}>
                                {autoEvalResult.communicator_type}
                            </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" mb={3}>
                            {ae
                                ? "Compare with your starting score. Notice the changes in your confidence!"
                                : "Compare avec ton score de départ. Remarque tes progrès en confiance !"}
                        </Typography>

                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ borderRadius: 2, py: 1.5, bgcolor: '#bfd56c', color: '#000', fontWeight: 800 }}
                            onClick={handleComplete}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                        >
                            {ae ? "Save & Continue" : "Sauvegarder & Continuer"}
                        </Button>
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0 }}>
                {!testCompleted && selectedIds.length === 3 && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            const result = calculateAutoEval(selectedIds, autoEvalLang);
                            if (result) {
                                setAutoEvalResult(result.communicator_type);
                                setTestCompleted(true);
                            }
                        }}
                        sx={{ borderRadius: 2, bgcolor: '#0f172a', color: 'white', fontWeight: 700 }}
                    >
                        {ae ? "See Results" : "Voir les résultats"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FinalAutoEvaluationModal;
